import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  RotateCcw,
  MoreVertical,
  Eye,
  Trash2,
  Edit,
  Filter,
  ArrowRightLeft,
  Calendar,
  Store,
  Truck,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  Download,
  Printer as PrintIcon,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import { all_routes } from "../../Router/all_routes";
import { toast } from "sonner";
import { cn, formatDate } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { PageContainer } from "@/components/layout/PageContainer";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ReactSelect } from "@/components/ui/react-select";
import { TransferDetailsModal } from "./components/TransferDetailsModal";
import RecordNotFound from "@/core/components/RecordNotFound";

const StockTransfers = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const [searchText, setSearchText] = useState("");
  const [sourceStoreId, setSourceStoreId] = useState("");
  const [destStoreId, setDestStoreId] = useState("");
  const [status, setStatus] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    description: "",
    onConfirm: () => {},
    variant: "default",
    showReasonInput: false,
  });
  const [rejectionReason, setRejectionReason] = useState("");
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentPage(1);
  }, [searchText, sourceStoreId, destStoreId, status]);

  // Queries
  const { data: stores = [] } = useQuery({
    queryKey: ["stores"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.STORES.STORES);
      return res.data.data || [];
    },
  });

  const currentUser = useSelector((state) => state.user_data);

  const { data: transfersData, isLoading } = useQuery({
    queryKey: [
      "stockTransfers",
      currentPage,
      searchText,
      sourceStoreId,
      destStoreId,
      status,
      currentUser?.id,
      hasPermission("stock transfer view_all"),
    ],
    queryFn: async () => {
      const params = {
        page: currentPage,
        search: searchText,
        source_store_id: sourceStoreId,
        destination_store_id: destStoreId,
        status,
        per_page: 10,
        with: [
          "sourceStore",
          "destinationStore",
          "creator",
          "approver",
          "processor",
          "items.product",
          "items.inventoryBatch",
          "items.inventorySerial",
        ],
      };

      if (
        !hasPermission("stock transfer view_all") &&
        hasPermission("stock transfer view_own")
      ) {
        params.user_id = currentUser?.id;
      }

      const res = await apiClient.get(API_ENDPOINTS.STOCK.TRANSFERS, {
        params,
      });
      return res.data;
    },
  });

  const transfers = transfersData?.data || [];
  const paginationMeta = {
    current_page:
      transfersData?.meta?.current_page || transfersData?.current_page || 1,
    last_page: transfersData?.meta?.last_page || transfersData?.last_page || 1,
    total: transfersData?.meta?.total || transfersData?.total || 0,
  };

  // Mutations
  const cancelTransferMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.post(API_ENDPOINTS.STOCK.TRANSFER_CANCEL(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stockTransfers"] });
      toast.success("Transfer cancelled successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to cancel transfer");
    },
  });

  const processTransferMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.post(API_ENDPOINTS.STOCK.TRANSFER_PROCESS(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stockTransfers"] });
      toast.success("Transfer processed (Sent to In Transit)");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to process transfer",
      );
    },
  });

  const submitTransferMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.post(API_ENDPOINTS.STOCK.TRANSFER_SUBMIT(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stockTransfers"] });
      toast.success("Transfer submitted for approval");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to submit transfer");
    },
  });

  const approveTransferMutation = useMutation({
    mutationFn: async ({ id, action, reason }) => {
      return apiClient.put(API_ENDPOINTS.STOCK.TRANSFER_APPROVAL(id), {
        action,
        reason,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["stockTransfers"] });
      toast.success(`Transfer ${variables.action}d successfully`);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to process approval",
      );
    },
  });

  const downloadPdfMutation = useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.STOCK_TRANSFER_DOCUMENT(id),
        { responseType: "blob" },
      );
      return response.data;
    },
    onSuccess: (data) => {
      const url = window.URL.createObjectURL(
        new Blob([data], { type: "application/pdf" }),
      );
      setPdfBlobUrl(url);
      setShowPdfModal(true);
      toast.success("Document generated successfully!");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to download document",
      );
    },
  });

  const handleDownloadPDF = () => {
    if (pdfBlobUrl) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = `stock-transfer-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("PDF downloaded successfully!");
    }
  };

  const handlePrintPDF = () => {
    if (pdfBlobUrl) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfBlobUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
      toast.info("Opening print dialog...");
    }
  };

  const handleClosePdfModal = () => {
    setShowPdfModal(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  const handleCancel = (id) => {
    setConfirmDialog({
      open: true,
      title: "Are you sure?",
      description: "This will cancel the stock transfer request.",
      onConfirm: () => cancelTransferMutation.mutate(id),
      variant: "destructive",
      showReasonInput: false,
    });
  };

  const handleProcess = (id) => {
    setConfirmDialog({
      open: true,
      title: "Process Transfer?",
      description:
        "This will execute the stock movement and mark it as processed.",
      onConfirm: () => processTransferMutation.mutate(id),
      variant: "default",
      showReasonInput: false,
    });
  };

  const handleSubmitApproval = (id) => {
    setConfirmDialog({
      open: true,
      title: "Submit for Approval?",
      description: "This will submit the transfer request for approval.",
      onConfirm: () => submitTransferMutation.mutate(id),
      variant: "default",
      showReasonInput: false,
    });
  };

  const handleApproval = (id, action) => {
    setRejectionReason("");
    setConfirmDialog({
      open: true,
      title: `${action === "approve" ? "Approve" : "Reject"} Transfer?`,
      description: `Are you sure you want to ${action} this transfer?`,
      onConfirm: (reason) =>
        approveTransferMutation.mutate({
          id,
          action,
          reason: action === "reject" ? reason : "",
        }),
      variant: action === "approve" ? "success" : "destructive",
      showReasonInput: action === "reject",
    });
  };

  const columns = [
    {
      accessorKey: "transfer_number",
      header: "Transfer ID",
      cell: ({ row }) => (
        <span className="font-bold">{row.original.transfer_number}</span>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Date",
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {formatDate(row.original.created_at)}
        </span>
      ),
    },
    {
      header: "Movement",
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <div className="flex flex-col">
            <span className="text-xs font-medium uppercase text-muted-foreground">
              From
            </span>
            <Badge variant="outline" className="bg-muted/50">
              {row.original.source_store?.name}
            </Badge>
          </div>
          <ArrowRightLeft size={14} className="text-muted-foreground mt-4" />
          <div className="flex flex-col">
            <span className="text-xs font-medium uppercase text-muted-foreground">
              To
            </span>
            <Badge
              variant="outline"
              className="border-green-200 bg-green-50 text-green-700"
            >
              {row.original.destination_store?.name}
            </Badge>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "total_items",
      header: "Items",
      cell: ({ row }) => (
        <span className="font-bold">{row.original.total_items || 0}</span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const variants = {
          draft: "secondary",
          pending: "warning",
          approved: "info",
          rejected: "danger",
          processed: "success",
          cancelled: "danger",
        };

        if (!hasPermission("stock transfer read")) {
          return <RecordNotFound />;
        }
        return (
          <Badge
            variant={variants[row.original.status] || "outline"}
            className="capitalize"
          >
            {row.original.status?.replace("_", " ")}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {hasPermission("stock transfer read") && (
              <DropdownMenuItem
                className="gap-2"
                onClick={() => {
                  setSelectedTransfer(row.original);
                  setIsViewModalOpen(true);
                }}
              >
                <Eye size={14} /> View Details
              </DropdownMenuItem>
            )}

            {hasPermission("report stock transfer document view") && (
              <DropdownMenuItem
                className="gap-2 text-primary"
                onClick={() => {
                  toast.info("Generating PDF...");
                  downloadPdfMutation.mutate(row.original.id);
                }}
              >
                <Download size={14} /> Download Document
              </DropdownMenuItem>
            )}

            {row.original.status === "draft" && (
              <>
                {hasPermission("stock transfer submit_for_approval") && (
                  <DropdownMenuItem
                    className="gap-2 text-blue-600"
                    onClick={() => handleSubmitApproval(row.original.id)}
                  >
                    <CheckCircle size={14} /> Submit for Approval
                  </DropdownMenuItem>
                )}
                {hasPermission("stock transfer update") && (
                  <DropdownMenuItem
                    className="gap-2 text-primary"
                    onClick={() =>
                      navigate(`/edit-transfer/${row.original.id}`)
                    }
                  >
                    <Edit size={14} /> Edit Transfer
                  </DropdownMenuItem>
                )}
                {hasPermission("stock transfer cancel") && (
                  <DropdownMenuItem
                    className="gap-2 text-destructive"
                    onClick={() => handleCancel(row.original.id)}
                  >
                    <Trash2 size={14} /> Cancel Transfer
                  </DropdownMenuItem>
                )}
              </>
            )}

            {row.original.status === "pending" && (
              <>
                {hasPermission("stock transfer approve") && (
                  <>
                    <DropdownMenuItem
                      className="gap-2 text-green-600"
                      onClick={() => handleApproval(row.original.id, "approve")}
                    >
                      <CheckCircle size={14} /> Approve
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="gap-2 text-destructive"
                      onClick={() => handleApproval(row.original.id, "reject")}
                    >
                      <Trash2 size={14} /> Reject
                    </DropdownMenuItem>
                  </>
                )}
                {hasPermission("stock transfer update") && (
                  <DropdownMenuItem
                    className="gap-2 text-primary"
                    onClick={() =>
                      navigate(`/edit-transfer/${row.original.id}`)
                    }
                  >
                    <Edit size={14} /> Edit Transfer
                  </DropdownMenuItem>
                )}
                {hasPermission("stock transfer cancel") && (
                  <DropdownMenuItem
                    className="gap-2 text-destructive"
                    onClick={() => handleCancel(row.original.id)}
                  >
                    <Trash2 size={14} /> Cancel Transfer
                  </DropdownMenuItem>
                )}
              </>
            )}

            {row.original.status === "approved" && (
              <>
                {hasPermission("stock transfer process") && (
                  <DropdownMenuItem
                    className="gap-2 text-blue-600"
                    onClick={() => handleProcess(row.original.id)}
                  >
                    <Truck size={14} /> Process Transfer
                  </DropdownMenuItem>
                )}
                {hasPermission("stock transfer cancel") && (
                  <DropdownMenuItem
                    className="gap-2 text-destructive"
                    onClick={() => handleCancel(row.original.id)}
                  >
                    <Trash2 size={14} /> Cancel Transfer
                  </DropdownMenuItem>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const handleReset = () => {
    setSearchText("");
    setSourceStoreId("");
    setDestStoreId("");
    setStatus("");
    setCurrentPage(1);
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Stock Transfers"
          subtitle="Manage inter-store stock movements"
        />
        <PageHeaderActions>
          {hasPermission("stock transfer create") && (
            <Button onClick={() => navigate(all_routes.newTransfer)}>
              <Plus className="mr-2 h-4 w-4" /> New Transfer
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 w-full">
          <FilterSearch
            placeholder="Search by ID..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="md:col-span-1"
          />

          <ReactSelect
            options={stores.map((s) => ({
              value: String(s.id),
              label: s.name,
            }))}
            value={stores
              .map((s) => ({ value: String(s.id), label: s.name }))
              .find((o) => o.value === sourceStoreId)}
            onChange={(opt) => setSourceStoreId(opt ? opt.value : "")}
            placeholder="From Store"
            isClearable
          />

          <ReactSelect
            options={stores.map((s) => ({
              value: String(s.id),
              label: s.name,
            }))}
            value={stores
              .map((s) => ({ value: String(s.id), label: s.name }))
              .find((o) => o.value === destStoreId)}
            onChange={(opt) => setDestStoreId(opt ? opt.value : "")}
            placeholder="To Store"
            isClearable
          />

          <ReactSelect
            options={[
              { value: "draft", label: "Draft" },
              { value: "pending", label: "Pending" },
              { value: "approved", label: "Approved" },
              { value: "rejected", label: "Rejected" },
              { value: "processed", label: "Processed" },
              { value: "cancelled", label: "Cancelled" },
            ]}
            value={[
              { value: "draft", label: "Draft" },
              { value: "pending", label: "Pending" },
              { value: "approved", label: "Approved" },
              { value: "rejected", label: "Rejected" },
              { value: "processed", label: "Processed" },
              { value: "cancelled", label: "Cancelled" },
            ].find((o) => o.value === status)}
            onChange={(opt) => setStatus(opt ? opt.value : "")}
            placeholder="All Status"
            isClearable
          />

          <Button variant="outline" onClick={handleReset} className="gap-2">
            <RotateCcw size={16} /> Reset
          </Button>
        </div>
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={transfers}
          isLoading={isLoading}
          onRowClick={(row) => {
            setSelectedTransfer(row);
            setIsViewModalOpen(true);
          }}
          pagination={{
            pageIndex: currentPage - 1,
            pageSize: pageSize,
          }}
          pageCount={paginationMeta.last_page}
          onPaginationChange={(updater) => {
            if (typeof updater === "function") {
              const newState = updater({
                pageIndex: currentPage - 1,
                pageSize,
              });
              setCurrentPage(newState.pageIndex + 1);
              setPageSize(newState.pageSize);
            }
          }}
          footerInfo={
            <span className="font-medium text-foreground">
              Total Transfers: {paginationMeta.total}
            </span>
          }
        />
      </TableCard>

      {selectedTransfer && (
        <TransferDetailsModal
          isOpen={isViewModalOpen}
          onOpenChange={setIsViewModalOpen}
          transfer={selectedTransfer}
        />
      )}

      <AlertDialog
        open={confirmDialog.open}
        onOpenChange={(v) => setConfirmDialog((prev) => ({ ...prev, open: v }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.description}
            </AlertDialogDescription>
            {confirmDialog.showReasonInput && (
              <div className="mt-4 text-left">
                <Label
                  htmlFor="rejectionReason"
                  className="text-sm font-semibold"
                >
                  Reason for Rejection <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="rejectionReason"
                  placeholder="Enter a reason for rejecting this transfer..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="mt-2"
                />
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={cn(
                confirmDialog.variant === "destructive" &&
                  "bg-destructive text-destructive-foreground hover:bg-destructive/90",
                confirmDialog.variant === "success" &&
                  "bg-emerald-600 text-white hover:bg-emerald-700",
              )}
              onClick={(e) => {
                if (confirmDialog.showReasonInput && !rejectionReason.trim()) {
                  e.preventDefault();
                  toast.error("Please provide a rejection reason.");
                  return;
                }
                confirmDialog.onConfirm(rejectionReason);
                setConfirmDialog((prev) => ({ ...prev, open: false }));
              }}
            >
              Yes, Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PDF Generation Modal */}
      <Dialog open={showPdfModal} onOpenChange={setShowPdfModal}>
        <DialogContent className="max-w-md p-0 overflow-hidden border-none shadow-2xl">
          <div className="h-2 bg-primary" />
          <div className="p-8 space-y-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-20 w-20 bg-success/10 text-success rounded-full flex items-center justify-center shadow-inner">
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-2xl font-bold">
                  Document Generated!
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  The Stock Transfer Document is ready.
                </DialogDescription>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={handleDownloadPDF}
                size="lg"
                className="rounded-xl w-full"
              >
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
              <Button
                onClick={handlePrintPDF}
                variant="outline"
                size="lg"
                className="rounded-xl w-full"
              >
                <PrintIcon className="mr-2 h-4 w-4" /> Print
              </Button>
            </div>
          </div>
          <div className="bg-muted/50 p-4 text-center border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClosePdfModal}
              className="font-medium"
            >
              Dismiss
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default StockTransfers;
